// Hides .site-header AND the mobile nav toggle rail (.site-nav-root, via its own
// data-scroll-state - see layout/site-navigation.css) together on scroll-down, reveals both
// again on scroll-up (mobile only - this module just sets data-scroll-state on each root, CSS
// decides what that means at each breakpoint). A small threshold (SCROLL_DELTA) avoids flicker
// from the 1-2px jitter some mobile browsers report on every scroll event, and both stay visible
// while the mobile nav aside is open (data-site-nav[data-open="true"]) so the toggle can't slide
// away out from under someone mid-navigation in the open menu - CSS also backstops this itself
// (site-navigation.css forces .site-nav__header visible whenever [data-open="true"], regardless
// of scroll-state) in case this module's own open-check below is ever bypassed.
//
// Uses a plain scroll listener rather than IntersectionObserver (unlike scroll-reveal.ts) since
// this genuinely needs to know scroll *direction*, not just element visibility - throttled to
// one direction check per animation frame via a scheduled-flag rather than a timer, so rapid
// scroll events between frames collapse into a single update instead of stacking up.

const SCROLL_DELTA_THRESHOLD = 8;

export type HeaderState = "visible" | "hidden";

// The distance is measured from an anchor that follows the scroll in the current state's
// direction (down while hidden, up while visible), not from the previous frame - a slow scroll
// moves less than the threshold per frame and would otherwise never change state.
export function nextHeaderState(state: HeaderState, anchorY: number, scrollY: number): { state: HeaderState; anchorY: number } {
    if (state === "hidden") {
        if (scrollY > anchorY) {
            return { state, anchorY: scrollY };
        }
        return anchorY - scrollY > SCROLL_DELTA_THRESHOLD ? { state: "visible", anchorY: scrollY } : { state, anchorY };
    }
    if (scrollY < anchorY) {
        return { state, anchorY: scrollY };
    }
    return scrollY - anchorY > SCROLL_DELTA_THRESHOLD ? { state: "hidden", anchorY: scrollY } : { state, anchorY };
}

function isNavOpen(navRoot: HTMLElement | null): boolean {
    return navRoot?.dataset.open === "true";
}

export function initScrollHeader(): void {
    const header = document.querySelector<HTMLElement>(".site-header");
    const navRoot = document.querySelector<HTMLElement>("[data-site-nav]");
    if (!header && !navRoot) {
        return;
    }

    let state: HeaderState = "visible";
    let anchorY = window.scrollY;
    let scheduled = false;

    function setState(next: HeaderState): void {
        state = next;
        if (header) {
            header.dataset.scrollState = next;
        }
        if (navRoot) {
            navRoot.dataset.scrollState = next;
        }
    }

    function update(): void {
        scheduled = false;
        const currentScrollY = window.scrollY;

        if (isNavOpen(navRoot) || currentScrollY <= 0) {
            setState("visible");
            anchorY = currentScrollY;
            return;
        }

        const next = nextHeaderState(state, anchorY, currentScrollY);
        anchorY = next.anchorY;
        if (next.state !== state) {
            setState(next.state);
        }
    }

    window.addEventListener(
        "scroll",
        () => {
            if (!scheduled) {
                scheduled = true;
                requestAnimationFrame(update);
            }
        },
        { passive: true },
    );
}
